import {Controller} from 'stimulus';

export default class extends Controller {
  static targets = [
    'articlesContainer',
    'articleCount',
    'editorPanel',
    'previewPanel',
    'previewFrame',
    'previewToggleBtn',
    'previewToggleIcon',
    'previewToggleText',
  ];
  static values = {
    deleteUrl: String,
  };

  connect() {
    this.previewVisible = false;
    this.previewLoaded = false;
    this.updateArticleNumbers();
    this.movedArticleId = null;

    document.addEventListener('turbo:before-stream-render', (event) => {
      setTimeout(() => {
        this.updateArticleNumbers();
        if (this.movedArticleId) {
          this.scrollToArticle(this.movedArticleId);
          this.movedArticleId = null;
        } else {
          this.scrollToLast();
        }
      }, 100);
    });
  }

  togglePreview(event) {
    event?.preventDefault();
    this.previewVisible = !this.previewVisible;

    if (this.previewVisible) {
      // Toon preview - editor wordt 50%, preview wordt zichtbaar
      this.editorPanelTarget.classList.remove('col-12');
      this.editorPanelTarget.classList.add('col-lg-6');
      this.previewPanelTarget.classList.remove('d-none');
      this.previewPanelTarget.classList.add('col-lg-6');
      this.previewToggleIconTarget.classList.remove('fa-eye');
      this.previewToggleIconTarget.classList.add('fa-eye-slash');
      this.previewToggleTextTarget.textContent = 'Verberg';

      // Laad iframe alleen bij eerste keer tonen
      if (!this.previewLoaded && this.hasPreviewFrameTarget) {
        const src = this.previewFrameTarget.dataset.src;
        if (src) {
          this.previewFrameTarget.src = src;
          this.previewLoaded = true;
        }
      }
    } else {
      // Verberg preview - editor wordt 100%
      this.editorPanelTarget.classList.remove('col-lg-6');
      this.editorPanelTarget.classList.add('col-12');
      this.previewPanelTarget.classList.add('d-none');
      this.previewPanelTarget.classList.remove('col-lg-6');
      this.previewToggleIconTarget.classList.remove('fa-eye-slash');
      this.previewToggleIconTarget.classList.add('fa-eye');
      this.previewToggleTextTarget.textContent = 'Preview';
    }
  }

  async saveAndRefreshPreview() {
    const form = this.element.querySelector('form');
    if (!form) return;

    try {
      const formData = new FormData(form);
      const response = await fetch(form.action, {
        method: 'PATCH',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        if (this.hasPreviewFrameTarget && this.previewLoaded) {
          this.previewFrameTarget.contentWindow.location.reload();
        }
      } else {
        this.showError('Opslaan mislukt');
      }
    } catch (error) {
      console.error('Save error:', error);
      this.showError('Opslaan mislukt');
    }
  }

  async deleteArticle(articleId) {
    if (!this.hasDeleteUrlValue) return false;

    try {
      const url = this.deleteUrlValue.replace('ARTICLE_ID', articleId);
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'text/vnd.turbo-stream.html',
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Error deleting article:', error);
      return false;
    }
  }

  showError(message) {
    const toast = document.createElement('div');
    toast.className = 'alert alert-danger position-fixed';
    toast.style.cssText = 'bottom: 20px; right: 20px; z-index: 9999; padding: 10px 20px;';
    toast.innerHTML = `<i class="fas fa-exclamation-circle mr-2"></i>${message}`;
    document.body.appendChild(toast);

    setTimeout(() => toast.remove(), 4000);
  }

  scrollToLast() {
    const lastArticle = this.articlesContainerTarget.lastElementChild;
    if (lastArticle) {
      lastArticle.scrollIntoView({behavior: 'smooth', block: 'center'});
      this.flash(lastArticle);
    }
  }

  scrollToArticle(articleId) {
    const article = this.articlesContainerTarget.querySelector(`[data-article-id="${articleId}"]`);
    if (article) {
      article.scrollIntoView({behavior: 'smooth', block: 'center'});
      this.flash(article);
    }
  }

  trackMoveArticle(event) {
    const article = event.target.closest('.newsletter-article');
    if (article) {
      this.movedArticleId = article.dataset.articleId;
    }
  }

  toggleArticle(event) {
    event.preventDefault();
    const article = event.target.closest('.newsletter-article');
    const content = article.querySelector('.article-content');
    const icon = event.target.closest('.toggle-article')?.querySelector('i');

    content.classList.toggle('d-none');
    icon?.classList.toggle('fa-chevron-up');
    icon?.classList.toggle('fa-chevron-down');
  }

  expandAll(event) {
    event.preventDefault();
    this.articlesContainerTarget.querySelectorAll('.article-content')
        .forEach((c) => c.classList.remove('d-none'));
    this.articlesContainerTarget.querySelectorAll('.toggle-article i')
        .forEach((i) => {
          i.classList.remove('fa-chevron-down');
          i.classList.add('fa-chevron-up');
        });
  }

  collapseAll(event) {
    event.preventDefault();
    this.articlesContainerTarget.querySelectorAll('.article-content')
        .forEach((c) => c.classList.add('d-none'));
    this.articlesContainerTarget.querySelectorAll('.toggle-article i')
        .forEach((i) => {
          i.classList.remove('fa-chevron-up');
          i.classList.add('fa-chevron-down');
        });
  }

  updateArticleNumbers() {
    const articles = this.articlesContainerTarget
        .querySelectorAll('.newsletter-article:not(.marked-for-removal)');

    articles.forEach((article, index) => {
      const numberEl = article.querySelector('.article-number');
      if (numberEl) numberEl.textContent = index + 1;

      const positionInput = article.querySelector('input[name*="[position]"]');
      if (positionInput) positionInput.value = index;
    });

    document.querySelectorAll('[data-newsletter-editor-target="articleCount"]').forEach((el) => {
      el.textContent = articles.length;
    });
  }

  updateTitlePreview(event) {
    const article = event.target.closest('.newsletter-article');
    const titlePreview = article?.querySelector('.article-title-preview');

    if (titlePreview) {
      const newTitle = event.target.value.trim();
      titlePreview.textContent = newTitle || 'Nieuw artikel';
      titlePreview.classList.toggle('text-muted', !newTitle);
      titlePreview.classList.toggle('font-italic', !newTitle);
    }

    this.schedulePreviewRefresh();
  }

  previewImage(event) {
    const file = event.target.files[0];
    if (!file) return;

    const article = event.target.closest('.newsletter-article');
    const imageUpload = article?.querySelector('.article-image-upload');
    const imageDisplay = imageUpload?.querySelector('.image-display');
    const uploadPlaceholder = imageUpload?.querySelector('.upload-placeholder');
    const removeField = imageUpload?.querySelector('.remove-image-field');

    if (removeField) removeField.value = '0';

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = imageDisplay?.querySelector('.displayed-image');
      if (img) {
        img.src = e.target.result;
        imageDisplay.style.display = 'inline-block';
        if (uploadPlaceholder) uploadPlaceholder.style.display = 'none';
      }
    };
    reader.readAsDataURL(file);
  }

  removeImage(event) {
    event.preventDefault();
    event.stopPropagation();

    const article = event.target.closest('.newsletter-article');
    if (!article) return;

    const imageUpload = article.querySelector('.article-image-upload');
    const imageDisplay = imageUpload?.querySelector('.image-display');
    const uploadPlaceholder = imageUpload?.querySelector('.upload-placeholder');
    const removeField = imageUpload?.querySelector('.remove-image-field');
    const fileInput = imageUpload?.querySelector('input[type="file"]');

    if (removeField) removeField.value = '1';
    if (imageDisplay) imageDisplay.style.display = 'none';
    if (uploadPlaceholder) uploadPlaceholder.style.display = 'block';
    if (fileInput) fileInput.value = '';
  }

  async markForRemoval(event) {
    event.preventDefault();
    const article = event.target.closest('.newsletter-article');
    const articleId = article.dataset.articleId;

    if (!articleId) return;

    if (!confirm('Weet je zeker dat je dit artikel wilt verwijderen?')) return;

    article.style.opacity = '0.5';

    const deleted = await this.deleteArticle(articleId);
    if (deleted) {
      article.remove();
      this.updateArticleNumbers();
      this.schedulePreviewRefresh();
    } else {
      article.style.opacity = '1';
      alert('Er ging iets mis bij het verwijderen');
    }
  }

  removeNewArticle(event) {
    event.preventDefault();
    const article = event.target.closest('.newsletter-article');

    if (confirm('Weet je zeker dat je dit artikel wilt verwijderen?')) {
      article.remove();
      this.updateArticleNumbers();
      this.schedulePreviewRefresh();
    }
  }

  flash(element) {
    element.style.backgroundColor = '#e8f4ff';
    setTimeout(() => element.style.backgroundColor = '', 500);
  }

  inputChanged() {
    this.schedulePreviewRefresh();
  }
}
